/*@flow*/
import type { Element } from 'react'; /** @jsx jsx */
import { css, jsx } from '@emotion/core';
import styled from '@emotion/styled';
import {
  useEffect,
  useReducer,
  useContext,
  Fragment,
  useState,
  createRef,
} from 'react';
import api from 'lib/api';
import qs from 'qs';
import Select from 'react-select';
import EmptyArray from 'lib/EmptyArray';
import intersectGeoJSON from 'lib/intersectGeoJSON';
import startsWith from 'lib/startsWith';
import * as I from 'immutable';
import useCachedQuery from 'lib/useCachedQuery';
import { useMutation } from '@apollo/react-hooks';
import { Popover, Overlay } from 'react-bootstrap';
import OverlayFactory from 'comps/OverlayFactory';
import useLeftToolbarCommands from 'comps/Left2/useCommands';
import useLeftToolbarContextState from 'comps/Left2/useContextState';
import hash from 'lib/hash';
import naturalCmp from 'underscore.string/naturalCmp';
import PATHS from 'const/paths';
import SetupAlerts from 'comps/City/SetupAlerts';
import { q } from 'ops';
import L from 'leaflet';
import ShareMap from '../ShareMap';
import useHistory from 'lib/useHistory';
import cx from 'classnames';
import useStoreState from 'lib/useStoreState';
import getDisabled from './getDisabled';
import useCommands from './useCommands';
import useContextState from './useContextState';
import reducer from './reducer';
import LoadingOverlay from './LoadingOverlay';
import parseError from './parseError';
import {
  GET_MY_AREAS,
  DELETE_AREA,
  UPDATE_DISTRICT,
  SHARE_MAP_OF_DISTRICT,
  refetchQueries,
} from './queries';
import { BLUE, RED, GRAY, GREEN } from './constants';
import AreaContext from './context';
import mkState from './mkState';
import EditOverlay from './EditOverlay';
import useInit from './useInit';
import useQuota from './useQuota';
import UpgradeCard from './UpgradeCard';
import type { StateT } from './types';
import usePrimaryDistrictId from './usePrimaryDistrictId';
import downloadURL from 'lib/downloadURL';
import dasherize from 'underscore.string/dasherize';
import delay from 'lib/delay';

const PopoverWrapper = OverlayFactory(Popover);

const Row = styled.div(
  (props) => `
  padding: 0 10px 2px;
  border-bottom: solid 1px #ccc !important;
  display: grid;
  grid-template-columns: 50px 1fr;
  align-items: center;
  min-height: 4.2rem;
  grid-gap: 10px;

  & > span:first-of-type {
    font-size: 1rem;
    text-transform: uppercase;
    font-weight: bold;
    text-align: right;
  }

  ${props.styles || ""}
  ${props.disabled ? "color: #6394c4 !important;" : ""}
`
);

const isParcel = (v) => v && v.get('name', '').match('Parcel');

const Header = () => (
  <Row>
    <div
      css={css(`
        font-size: 1.2rem;
        font-weight: bold;
        text-transform: uppercase;
      `)}
    >
      AREAS
    </div>
    <div
      css={css(`
        padding-right: 15px;
        text-align: right;
        color: #999;
        font-size: 1rem;
    `)}
    />
  </Row>
);

const ModeButton = styled.button(
  `
  background-color: ${BLUE} !important;
  border-bottom: solid 1px ${BLUE} !important;
  color: white !important;
  display: block;
  text-align: center;
  margin: 0;
  padding: 15px;
  border: none;
  font-weight: bold;
  line-height: 1.4rem;
  letter-spacing: 4px;
  word-spacing: 1px;
  vertical-align: middle;
  touch-action: manipulation;
  white-space: nowrap;
  user-select: none;
  text-decoration: none !important;
  box-shadow: none !important;
  border-collapse: collapse !important;
  text-transform: uppercase;
  width: 100%;
  &.active {
    background: rgba(17, 129, 251, 0.7) !important;
  }
`,
  (props) => props.styles || ''
);

const CreateAreaButtons = () => {
  const ctx = useContext(AreaContext);
  const isDisabled = getDisabled(ctx.state);
  const cmd = useCommands();
  return (
    <div
      css={css(`
      display: grid;
      grid-template-columns: 1fr 1fr;
    `)}
    >
      <ModeButton
        data-testid="CreateNewAreaButton"
        disabled={isDisabled('CREATE_NEW_AREA_BUTTON')}
        onClick={() => cmd.showEditOverlay()}
      >
        Save
      </ModeButton>
      <CancelButton />
    </div>
  );
};

const ReadyButtons = () => {
  const ctx = useContext(AreaContext);
  const { areaWorkingCopy } = useContextState();
  const { geojson, id } = areaWorkingCopy;
  const isDisabled = getDisabled(ctx.state);
  const cmd = useCommands();
  if (geojson && !id) return <CreateAreaButtons />;

  return (
    <ModeButton
      disabled={isDisabled('NEW_AREA_BUTTON')}
      onClick={() => {
        if (geojson) {
          cmd.clearArea();
        } else {
          cmd.startDrawing();
        }
      }}
      data-testid={geojson ? 'ClearAreaButton' : 'NewAreaButton'}
      styles={
        geojson
          ? `
        background-color: ${GRAY} !important;
        border-bottom-color: ${GRAY} !important;
      `
          : ''
      }
    >
      {geojson ? 'Clear Area' : 'New Area'}
    </ModeButton>
  );
};

const DrawingButtons = () => {
  const opts = { refetchQueries };
  const [save] = useMutation(UPDATE_DISTRICT, opts);
  const cmd = useCommands();

  const onClick = () => cmd.finish(save);

  return (
    <div
      css={css(`
      display: grid;
      grid-template-columns: 1fr 1fr;
    `)}
    >
      <ModeButton data-testid="FinishButton" onClick={onClick}>
        Done
      </ModeButton>
      <CancelButton />
    </div>
  );
};

const CancelButton = () => {
  const cmd = useCommands();
  return (
    <ModeButton
      onClick={() => {
        cmd.cancel();
      }}
      data-testid="CancelButton"
      styles={`
        border-left: solid 1px white;
        background-color: white !important;
        border-bottom: solid 1px #ccc !important;
        color: ${RED} !important;
      `}
    >
      Cancel
    </ModeButton>
  );
};

const ModeButtonGroup = () => {
  const ctx = useContext(AreaContext);
  const { mode } = ctx.state;
  if (mode === 'DRAWING') {
    return <DrawingButtons />;
  } else if (mode === 'READY') {
    return <ReadyButtons />;
  }
  return null;
};

const dot = (color = '#ccc', content = '"\f111"') => ({
  alignItems: 'center',
  display: 'flex',
  ':before': {
    borderRadius: 10,
    marginRight: 8,
    height: 10,
    width: 10,
    color,
    content,
    fontFamily: '"Font Awesome 5 Pro"',
    fontWeight: 900,
    display: 'inline-block',
    fontStyle: 'normal',
    fontVariant: 'normal',
    textRendering: 'auto',
    lineHeight: 1,
    fontSize: '1rem',
  },
});

const dotMap = (color = '#ccc') => dot(color, '"\f279"');

const getDotStyle = (data) => {
  const c = data.color;
  return data.dashboardId ? dotMap(c) : dot(c);
};

const ErrorPanel = ({ error }) => {
  console.error(error);

  return (
    <div className="text-danger p-3 border border-danger m-1">
      Error: {parseError(error).join('\n')}
    </div>
  );
};

const SelectArea = () => {
  const ctx = useContext(AreaContext);
  const cmd = useCommands();
  const primaryDistrictId = usePrimaryDistrictId();
  function isPrimary(id) {
    return primaryDistrictId === id;
  }
  const { areaWorkingCopy, mode } = ctx.state;
  const { data, loading, error } = useCachedQuery(GET_MY_AREAS);
  const { id } = areaWorkingCopy;
  const districts = I.getIn(data, ['me', 'districts'], []);

  // When the id or districts change, select and load the area with id.
  useEffect(() => {
    if (!id) return;
    const district = districts.find((v) => String(v.id) === String(id));
    if (district) {
      cmd.selectArea(district);
      // Longer timeout is necessary for when the page is first loading.
      centerMapTo(district.geojson, { wait: 3000 });
    }
  }, [id, hash(districts)]);

  if (error) return <ErrorPanel error={error} />;

  let options = I.List(districts)
    .sort((v1, v2) => naturalCmp(v1.name.toLowerCase(), v2.name.toLowerCase()))
    .map((v) => ({
      ...v,
      label: `${isPrimary(v.id) ? '[Default] ' : ''}${v.name}`,
      value: v.id,
    }))
    .toArray();
  const value =
    options.find((v) => id && String(v.value) === String(id)) || null;
  const isDisabled = mode === 'DRAWING' || EmptyArray.is(options);

  function onChange(v) {
    if (v) {
      cmd.set('areaWorkingCopy.id', v.id);
      centerMapTo(v.geojson, { wait: 1000 });
    } else {
      cmd.clearArea();
    }
  }

  return (
    <Select
      value={value}
      classNamePrefix="AreaSelect"
      placeholder={
        loading ? '...' : EmptyArray.is(options) ? 'No areas' : 'Select Area'
      }
      isClearable
      isDisabled={isDisabled}
      isLoading={loading}
      options={options}
      isSearchable={false}
      onChange={onChange}
      noOptionsMessage={({ inputValue }) =>
        inputValue ? 'No areas matching `' + inputValue + '`.' : 'No areas.'
      }
      styles={{
        menu: (v) => {
          return { ...v, marginTop: 0, zIndex: 999 };
        },
        menuList: (v) => {
          return {
            ...v,
            marginTop: -2,
            paddingTop: 0,
            paddingBottom: 0,
          };
        },
        indicatorsContainer: (v) => {
          return { ...v, paddingRight: 12 };
        },
        control: (v) => {
          const borderColor = 'hsl(0, 0%, 80%)';
          return {
            ...v,
            boxShadow: 'none',
            borderTop: 'none',
            borderLeft: 'none',
            borderRight: 'none',
            borderColor,
            '&:hover': {
              borderColor,
            },
          };
        },
        option: (styles, o) => ({
          ...styles,
          ...getDotStyle(o.data),
        }),
        singleValue: (styles, o) => ({
          ...styles,
          ...getDotStyle(o.data),
        }),
      }}
      theme={(theme) => ({
        ...theme,
        borderRadius: 0,
        colors: theme.colors,
      })}
    />
  );
};

// TODO: This could be better handled if map was controlled by the app state.
function centerMapTo(geo: string, o = { wait: 1000 }) {
  if (!geo) return;
  if (!window.cityMap) return;
  let geojson = JSON.parse(geo);
  const ms = o ? o.wait || 1000 : 1000;
  const bounds = L.geoJson(geojson).getBounds();
  try {
    setTimeout(() => {
      window.cityMap.fitBounds(bounds, { maxZoom: 22 });
    }, ms);
  } catch (e) {
    window.Rollbar.error(e, bounds, window.cityMap.getBounds(), geojson);
  }
}

const CreateMapButton = ({ showImagesInListView, sortByLastUpdated }) => {
  const cmd = useCommands();
  const initLoading = useInit(['CreateMapButton', 'isLoading'], false);
  const [isLoading, setLoading] = useState(initLoading);
  const s = useContextState();
  const id = s.getIn(['areaWorkingCopy', 'id']);
  const initErrors = useInit(['CreateMapButton', 'errors'], null);
  const [errors, setErrors] = useState(initErrors);
  const [shareMap] = useMutation(SHARE_MAP_OF_DISTRICT, {
    variables: {
      id,
      showImagesInListView,
      sortByLastUpdated,
    },
    refetchQueries,
  });

  const hasError = errors && errors.length > 0;

  const onError = (err) => {
    if (err.graphQLErrors) {
      setErrors(err.graphQLErrors.map((v) => v.message));
    } else if (err.message) {
      setErrors([err.message]);
    } else if (Array.isArray(err)) {
      setErrors(err);
    } else {
      setErrors([err]);
    }
    setLoading(false);
  };

  return (
    <div className="mt-4">
      {hasError ? (
        <div
          css={css('max-width: 200px;')}
          className="border-top mb-2 text-danger"
        >
          Error: {errors}
        </div>
      ) : null}
      <button
        className="btn btn-primary btn-block"
        onClick={() => {
          if (isLoading) return;
          setLoading(true);
          // Create dashboard for district
          // Uupdate working copy (which will now include dashboardId)
          // Show loading indicator in the button
          shareMap()
            .then((payload) => {
              setLoading(false);
              const key = 'districtConvert';
              const res = I.getIn(payload, ['data', key], {});

              if (res.success) {
                // Clear errors
                setErrors(null);
                // Copy working copy to backup
                cmd.mapShared(res.record);
              } else {
                setErrors(res.errors);
              }
            })
            .catch(onError);
        }}
        disabled={isLoading}
        data-testid="CreateMapButton"
      >
        {isLoading ? (
          <i className="fas fa-circle-notch fa-spin" />
        ) : (
          <i className="far fa-map" />
        )}{' '}
        {isLoading ? 'Creating...' : 'Create Map'}
      </button>
    </div>
  );
};

const ConfigureMap = ({
  showImagesInListView,
  setShowImagesInListView,
  setSortByLastUpdated,
  sortByLastUpdated,
}) => {
  const s = useContextState();
  const id = s.getIn(['areaWorkingCopy', 'dashboardId']) || '';
  return (
    <Fragment>
      <ShareMap
        id={id}
        key={id}
        showImagesInListView={showImagesInListView}
        setShowImagesInListView={setShowImagesInListView}
        sortByLastUpdated={sortByLastUpdated}
        setSortByLastUpdated={setSortByLastUpdated}
      />
      {!id && (
        <CreateMapButton
          showImagesInListView={showImagesInListView}
          sortByLastUpdated={sortByLastUpdated}
        />
      )}
    </Fragment>
  );
};

const MaxMapsReached = () => (
  <UpgradeCard
    message="You have reached the maximum number of maps allowed by your plan. Please contact us to upgrade."
    emailBody="I'd like to upgrade my account to create more maps."
    data-testid="MaxMaps"
  />
);

const ShareMapOverlay = ({
  showImagesInListView,
  setShowImagesInListView,
  setSortByLastUpdated,
  sortByLastUpdated,
}) => {
  const s = useContextState();
  const dashboardId = s.getIn(['areaWorkingCopy', 'dashboardId']);
  const { hasRoom, loading } = useMapQuota();
  const showUpgradeCard = !dashboardId && !hasRoom;
  if (loading) return <LoadingOverlay />;

  // Show Upgrade message when the user does not have enough
  // capacity in his account
  return showUpgradeCard ? (
    <MaxMapsReached />
  ) : (
    <ConfigureMap
      showImagesInListView={showImagesInListView}
      setShowImagesInListView={setShowImagesInListView}
      sortByLastUpdated={sortByLastUpdated}
      setSortByLastUpdated={setSortByLastUpdated}
    />
  );
};

const useDisabled = (v) => {
  const ctx = useContext(AreaContext);
  const isDisabled = getDisabled(ctx.state);
  return isDisabled(v);
};

async function getYelpServiceQueryParams(
  geojson: string
): Promise<{ radius: number, latitude: number, longitude: number }> {
  const geo = L.geoJson(geojson);
  const bounds = geo.getBounds();
  const { lat: latitude, lng: longitude } = bounds.getCenter();
  const diameter = bounds.getNorthWest().distanceTo(bounds.getNorthEast());
  const radius = Math.round(diameter / 2);
  return Promise.resolve({ radius, latitude, longitude });
}

async function getYelpServiceUrl(args: {
  radius: number,
  latitude: number,
  longitude: number,
}): Promise<string> {
  const params = qs.stringify({ yelp: args });
  const url = `/data/yelp?${params}`;
  return Promise.resolve(url);
}

async function getYelpServiceFileLink(dataUrl: string) {
  const opts = {
    credentials: 'omit',
    headers: {
      'Content-type': 'application/json',
    },
  };
  const { s3FileLink } = await api.fetchJson(dataUrl, opts);
  if (s3FileLink) return s3FileLink;
  await delay(1000);
  return getYelpServiceFileLink(dataUrl);
}

function DownloadFromYelpButton(props: {
  areaName: string,
  geojson: ?string,
  disabled: boolean,
}) {
  const { areaName, geojson, disabled } = props;
  const [isLoading, setLoading] = useState(false);

  async function downloadYelpData() {
    if (!geojson) return;
    setLoading(true);
    let timer;

    try {
      const params = await getYelpServiceQueryParams(JSON.parse(geojson));
      const dataUrl = await getYelpServiceUrl(params);

      timer = setTimeout(() => {
        throw new Error('Timeout');
      }, 10000);

      const link = await getYelpServiceFileLink(dataUrl);

      if (link) {
        downloadURL({ url: link });
      }
      clearTimeout(timer);
    } catch (e) {
      console.error(e, props);
      setLoading(false);
      clearTimeout(timer);
    }
    setLoading(false);
  }

  return (
    <button
      disabled={disabled}
      onClick={() => {
        if (disabled) return;
        downloadYelpData();
      }}
      data-testid="DownloadYelpData"
      css={css(`color: ${GREEN} !important;`)}
    >
      <i
        className={cx({
          'fab fa-yelp mr-2': !isLoading,
          'fa fa-spinner fa-spin': isLoading,
        })}
      />{' '}
      {isLoading ? 'Loading...' : 'Download Yelp Data'}
    </button>
  );
}

const Features = ({
  showImagesInListView,
  setShowImagesInListView,
  setSortByLastUpdated,
  sortByLastUpdated,
}) => {
  const alertsButton = createRef();
  const mapsButton = createRef();
  const init1 = useInit(['Features', 'showMapsOverlay'], false);
  const init2 = useInit(['Features', 'showAlertsOverlay'], false);
  const [showMapsOverlay, setShowMapsOverlay] = useState(init1);
  const [showAlertsOverlay, setShowAlertsOverlay] = useState(init2);
  const canShareMaps = !useDisabled('SHARE_MAPS_BUTTON');
  const canShowAlerts = !useDisabled('ALERTS_BUTTON');
  const s = useContextState();
  const id = s.getIn(['areaWorkingCopy', 'id']);
  const geojson = s.getIn(['areaWorkingCopy', 'geojson']);
  const areaName = s.getIn(['areaWorkingCopy', 'name'], 'area');
  const isTablesBtnDisabled = useDisabled('TABLES_BUTTON');

  return (
    <div>
      <div
        css={css(`
        padding: 0 10px;
        border-bottom: none;
        font-size: 0.9rem;
        line-height: 2rem;
      `)}
      >
        <span>AREA FEATURES</span>
      </div>
      <div
        css={css(`
      border-bottom: none;
      button {
        color: ${GREEN};
      }
  `)}
      >
        <div
          data-testid="ShareMapsButtonContainer"
          ref={mapsButton}
          className="d-flex justify-content-between"
        >
          <button
            disabled={!canShareMaps}
            onClick={() => {
              canShareMaps && setShowMapsOverlay(true);
            }}
            data-testid="ShareMapButton"
            css={css(`
              color: ${GREEN} !important;
            `)}
          >
            <i className="far fa-map" /> Share Maps
          </button>
          <DownloadFromYelpButton
            areaName={areaName}
            disabled={!canShareMaps}
            geojson={geojson}
          />
        </div>
        <div
          css={css(`
           display: grid;
           grid-template-columns: 1fr 1fr;
           button { display: block; width: 100%; }
         `)}
        >
          <button
            disabled={!canShowAlerts}
            onClick={() => canShowAlerts && setShowAlertsOverlay(true)}
            ref={alertsButton}
            data-testid="ShowAlertsButton"
            className="text-left"
          >
            <span>
              <i className="far fa-bell" /> Setup Alerts
            </span>
          </button>
          <button
            onClick={() => {
              if (isTablesBtnDisabled) return;
              if (!id) return;
              const path = `/tables?filtered[0][id]=area_id&filtered[0][value]=${id}`;
              Turbolinks.visit(path);
            }}
            disabled={isTablesBtnDisabled}
            data-testid="ShowTablesButton"
            className="text-right"
          >
            <span>Import Data >></span>
          </button>
        </div>

        <Overlay
          placement="bottom"
          rootClose
          target={() => alertsButton.current}
          container={this}
          show={showAlertsOverlay}
          onHide={() => setShowAlertsOverlay(false)}
        >
          <Popover
            id="alerts-options"
            style={{ maxWidth: 'initial', width: 250 }}
          >
            {id && <SetupAlerts districtId={id} />}
          </Popover>
        </Overlay>
      </div>
      <Overlay
        placement="left"
        rootClose
        target={() => mapsButton.current}
        container={this}
        show={showMapsOverlay}
        onHide={() => setShowMapsOverlay(false)}
        transition={false}
      >
        <Popover id="shared-maps" style={{ maxWidth: "initial", width: 250 }}>
          <ShareMapOverlay
            showImagesInListView={showImagesInListView}
            setShowImagesInListView={setShowImagesInListView}
            sortByLastUpdated={sortByLastUpdated}
            setSortByLastUpdated={setSortByLastUpdated}
          />
        </Popover>
      </Overlay>
    </div>
  );
};

const useMapQuota = () => useQuota('Dashboard');

const CommitAreaDeletionButton = () => {
  const cmd = useCommands();
  const opts = { refetchQueries };
  const [deleteArea] = useMutation(DELETE_AREA, opts);
  const initLoading = useInit(['CommitAreaDeletionButton', 'isLoading'], false);
  const [isLoading, setLoading] = useState(initLoading);
  const icon = isLoading ? <i className="fas fa-circle-notch fa-spin" /> : null;

  const onClick = () => {
    setLoading(true);
    cmd.deletionRequested(deleteArea);
  };

  return (
    <button
      onClick={onClick}
      disabled={isLoading}
      className="btn mt-2 font-weight-bold d-block"
      data-testid="CommitAreaDeletionButton"
      css={css(`
        background: ${RED};
        color: white !important;
      `)}
    >
      {icon} {isLoading ? 'Deleting...' : 'Delete'}
    </button>
  );
};

const Tools = ({
  showImagesInListView,
  setShowImagesInListView,
  setSortByLastUpdated,
  sortByLastUpdated,
}) => {
  const ctx = useContext(AreaContext);
  const editButtonRef = createRef();
  const { showEditOverlay } = ctx.state;
  const s = useContextState();
  const id = s.getIn(['areaWorkingCopy', 'id'], null);
  const showDeleteOverlay = useInit(['Tools', 'showDeleteOverlay'], false);
  const isEditButtonDisabled = useDisabled('EDIT_AREA_BUTTON');
  const cmd = useCommands();
  const isCloneDisabled = useDisabled('CLONE_AREA_BUTTON');
  const isDeleteDisabled = useDisabled('DELETE_AREA_BUTTON');

  return (
    <div
      css={css(`
        border-bottom: solid 1px #ccc;
        button {
          color: #333;
          text-decoration: uppercase;
          font-size: 1.2rem;
          padding: 5px 10px;
          text-decoration: none;
          display: inline-block;
        }
      `)}
    >
      <div
        css={css(`
        display: flex;
        align-items: center;
        justify-items: center;
        border-bottom: none;
        margin-bottom: 1rem !important;

        [data-testid='CloneAreaButton'] {
          color: ${isCloneDisabled ? 'initial' : GREEN + ' !important'};
        }
        [data-testid='DeleteAreaButton'] {
          color: ${isDeleteDisabled ? 'initial' : RED + ' !important'};
        }
        [data-testid='EditAreaButton'] {
          color: ${isEditButtonDisabled ? 'initial' : GREEN + ' !important'};
        }
      `)}
      >
        <button
          disabled={isEditButtonDisabled}
          onClick={() => {
            !showEditOverlay && cmd.showEditOverlay();
          }}
          ref={editButtonRef}
          data-testid="EditAreaButton"
        >
          <i className="fa fa-edit" /> Edit
        </button>
        <Overlay
          placement="left"
          rootClose
          target={() => editButtonRef.current}
          container={this}
          show={showEditOverlay}
          transition={false}
          onHide={() => {
            cmd.setEditOverlay(false);
            // Clear name if id is false.
            if (!id) {
              cmd.editName('');
            }
          }}
        >
          <Popover id="edit-tooltip" style={{ maxWidth: 'initial' }}>
            <EditOverlay />
          </Popover>
        </Overlay>
        <button
          disabled={isCloneDisabled}
          onClick={() => {
            if (isCloneDisabled) return;
            cmd.cloneArea();
            setTimeout(() => cmd.setEditOverlay(true), 30);
          }}
          data-testid="CloneAreaButton"
        >
          <i className="far fa-clone" /> Duplicate
        </button>
        <PopoverWrapper
          content={
            <div>
              <div>
                Deleting an area is permanent. All your shared maps will be
                deleted as well. Are you sure?
              </div>
              <div>
                <CommitAreaDeletionButton />
              </div>
            </div>
          }
          placement="left"
          id="delete-tooltip"
          trigger={['click']}
          disabled={useDisabled('DELETE_AREA_BUTTON')}
          rootClose
          container={this}
          defaultShow={Boolean(showDeleteOverlay)}
          overrideTestHack
        >
          <button
            disabled={isDeleteDisabled}
            className="ml-auto"
            data-testid="DeleteAreaButton"
            css={css(`
              margin-right: 15px;
            `)}
          >
            <i className="fa fa-trash" />
          </button>
        </PopoverWrapper>
      </div>
      <Features
        showImagesInListView={showImagesInListView}
        setShowImagesInListView={setShowImagesInListView}
        sortByLastUpdated={sortByLastUpdated}
        setSortByLastUpdated={setSortByLastUpdated}
      />
    </div>
  );
};

const parseGeometry = (feature) => {
  if (!feature) return;
  if (typeof feature === 'string') {
    feature = JSON.parse(feature);
  }

  let geometry;
  if (feature.type === 'Feature') {
    geometry = feature.geometry;
  } else if (feature.type === 'FeatureCllection') {
    geometry = feature[0].geometry;
  } else {
    geometry = feature;
  }
  return JSON.stringify(geometry);
};

const useSyncPolygonWithParcelDeck = () => {
  const m = {
    geojsonString: (PATHS.areaPolygonGeoJSON: string),
  };
  const { geojsonString } = useStoreState(m);
  const geojson = geojsonString ? parseGeometry(geojsonString) : null;
  const [localGeoJson, setLocalGeoJSON] = useState(geojson);
  const s = useLeftToolbarContextState();
  const cmd = useLeftToolbarCommands();
  const { activeLayers } = s || {};

  useEffect(() => {
    setLocalGeoJSON(geojson);
  }, [hash(geojson)]);

  const intersectParcelDecks = (geojson2) => {
    // Intersect decks
    const mapDecks = (v3, k) => {
      // Only look in the layer decks.
      if (!startsWith('layer-').test(k)) return v3;
      // Only intersect parcel layers
      if (!isParcel(v3)) return v3;

      if (geojson2) {
        // Already intersected.
        if (v3.get('oldSql')) {
          v3 = v3.set('sql', v3.get('oldSql'));
        }
        // Intersect SQL
        //
        // Backup the old sql
        const query = v3.get('sql');
        v3 = v3.set('oldSql', query);
        v3 = v3.set('active', true);
        //
        // Update the query of the layer so it only covers
        // the intersection of area and the geojson.
        //
        const sql = intersectGeoJSON({ query, geojson: geojson2 });
        v3 = v3.set('sql', sql);
      } else {
        v3 = v3.set('active', false);
        if (v3.get('oldSql')) {
          v3 = v3.set('sql', v3.get('oldSql'));
          v3 = v3.delete('oldSql');
        }
      }
      return v3;
    };
    const updateDecks = (v2) => v2.map(mapDecks);

    cmd.update(['decks'], updateDecks);
    cmd.set(['loading'], true);
    // Give time for tiles to load
    setTimeout(() => cmd.set(['loading'], false), 3000);
  };

  // When the polygon geojson changes, toggle active parcel layers
  // and itnersect their SQL with geojson.
  useEffect(() => {
    const value = Boolean(geojson && localGeoJson);
    cmd.toggleParcelLayers(value);

    // Intersect parcels
    setTimeout(() => {
      intersectParcelDecks(localGeoJson);
    }, 100);
  }, [localGeoJson, geojson]);

  // When the activeLayers change, and the active layers include a
  // Parcel deck, intersect the SQL of the parcel decks.
  useEffect(() => {
    if (!activeLayers) return;
    if (activeLayers.filter(isParcel).isEmpty()) return;
    if (!localGeoJson) return;
    intersectParcelDecks(localGeoJson);
  }, [hash(activeLayers)]);
};

const useSyncAreaIdWithQuery = () => {
  const m = {
    areaId: '$query.areaId',
  };
  const { areaId } = useStoreState(m);

  useEffect(() => {
    q((v) => {
      if (areaId) {
        // Add areaId to query string
        v = v.update((v2) =>
          v2.set('filter', v2.get('filter', I.Map()).merge(I.Map({ areaId })))
        );
      } else {
        // Remove areaId from query string
        v = v.update((v2) => {
          return v2.has('filter') ? v2.deleteIn(['filter', 'areaId']) : v2;
        });
      }
      return v;
    });
  }, [areaId]);
};

// useQueryAreaId calls fn callback function when areaId changes in the query string.
const useQueryAreaId = (fn) => {
  const loc: { query: ?{ areaId: string } } = useHistory();
  useEffect(() => {
    if (!loc.query) return;
    const { query } = loc;
    const { areaId } = query;
    fn(areaId);
  }, [loc.query && loc.query.areaId]);
};

export const Component = (): Element<string> => {
  // Set the initial state for some of the checkboxes from the Share Map modal.
  const [showImagesInListView, setShowImagesInListView] = useState(false);
  const [sortByLastUpdated, setSortByLastUpdated] = useState(false);
  const ctx = useContext(AreaContext);
  const { areaWorkingCopy } = ctx.state;
  const { color, geojson } = areaWorkingCopy;
  const cmd = useCommands();

  // When color changes, reload polygon.
  useEffect(() => {
    if (!color || !geojson) return;
    cmd.reloadPolygon(JSON.parse(geojson), color);
  }, [color]);

  // When the areaId query parameter changes, assign it to working copy.
  useQueryAreaId((areaId) => {
    areaId && cmd.set('areaWorkingCopy.id', areaId);
  });

  // When the polygon changes, change the parcel decks.
  useSyncPolygonWithParcelDeck();

  // When the polygon changes, update the query string.
  useSyncAreaIdWithQuery();

  return (
    <div
      css={css(`
          button[disabled] { cursor: no-drop; }
          background: white;
        `)}
    >
      <Header />
      <ModeButtonGroup />
      <SelectArea />
      {areaWorkingCopy && (
        <Tools
          showImagesInListView={showImagesInListView}
          setShowImagesInListView={setShowImagesInListView}
          sortByLastUpdated={sortByLastUpdated}
          setSortByLastUpdated={setSortByLastUpdated}
        />
      )}
    </div>
  );
};

export function Container(
  props: $Shape<StateT & { children?: React$Node }> = {}
): any {
  // Accept props to seed the initState for testing.
  const [state, dispatch] = useReducer(reducer, mkState().mergeDeep(props));
  const ctx = { state, dispatch };

  return (
    <AreaContext.Provider value={ctx}>{props.children}</AreaContext.Provider>
  );
}

const ContainedComponent = (props: $Shape<StateT> = {}) => {
  return (
    <Container {...props}>
      <Component />
    </Container>
  );
};

export default ContainedComponent;
